import React, { createContext, useState, useCallback } from 'react'
import { getErrorMessage } from '../utils/api'

export interface Agent {
  id: string
  name: string
  email: string
  role: 'agent' | 'supervisor' | 'admin'
  /** Dev-only account — Dev Board access only, no support side. */
  dev_only?: boolean
  avatar_url?: string | null
}

interface AuthContextType {
  isAuthenticated: boolean
  currentAgent: Agent | null
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  clearError: () => void
  updateAgent: (patch: Partial<Agent>) => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

// Restore session from localStorage synchronously so ProtectedRoute works on first render
function getInitialAuth(): { token: string | null; agent: Agent | null; authenticated: boolean } {
  try {
    const savedToken = localStorage.getItem('auth_token')
    if (savedToken) {
      const parts = savedToken.split('.')
      if (parts.length !== 3) {
        localStorage.removeItem('auth_token')
        return { token: null, agent: null, authenticated: false }
      }
      let payload: any
      try {
        payload = JSON.parse(atob(parts[1]))
      } catch {
        localStorage.removeItem('auth_token')
        return { token: null, agent: null, authenticated: false }
      }
      if (typeof payload !== 'object' || !payload || typeof payload.exp !== 'number') {
        localStorage.removeItem('auth_token')
        return { token: null, agent: null, authenticated: false }
      }
      if (payload.exp * 1000 > Date.now()) {
        return {
          token: savedToken,
          agent: {
            id: payload.agent_id || payload.id || payload.agentId,
            name: payload.name || 'Agent',
            email: payload.email || '',
            role: payload.role || 'agent',
            dev_only: payload.dev_only || false,
            avatar_url: payload.avatar_url || null,
          },
          authenticated: true,
        }
      }
      localStorage.removeItem('auth_token')
    }
  } catch {
    localStorage.removeItem('auth_token')
  }
  return { token: null, agent: null, authenticated: false }
}


/** Three-role model (29 Jul 2026): the STORED role is claude_dev | dev | sales_support.
 * Every existing role check in the app compares against the legacy tiers, so we expose the
 * mapped tier as `role` (claude_dev→admin, others→agent) and keep the stored value as
 * `realRole` with a human label. Legacy values pass through untouched. */
// FINAL (Lee, 29 Jul 2026): every role maps to 'admin' — all admin features for everyone.
// The only distinction is realRole==='dev' (no CRM menu, lands in Dev Mode). Mapping
// unconditionally also shields the UI from broken/legacy role values in old tokens.
const ROLE_LABEL: Record<string, string> = { claude_dev: 'Claude Dev', dev: 'Dev', sales_support: 'Sales & Support', admin: 'Admin', supervisor: 'Supervisor', agent: 'Agent' }
const normalizeAgent = (a: any) => (a ? { ...a, realRole: a.realRole || a.role, role: 'admin', roleLabel: ROLE_LABEL[a.realRole || a.role] || 'Admin', devOnly: !!(a.devOnly || a.dev_only) } : a)

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const initial = getInitialAuth()
  const [isAuthenticated, setIsAuthenticated] = useState(initial.authenticated)
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(normalizeAgent(initial.agent))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [_token, setToken] = useState<string | null>(initial.token)

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/agents/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(typeof data.message === 'string' ? data.message : (typeof data.error === 'string' ? data.error : 'Authentication failed'))
      }

      const data = await response.json()
      setToken(data.token)
      localStorage.setItem('auth_token', data.token)
      setCurrentAgent(normalizeAgent(data.agent))
      setIsAuthenticated(true)
      // Fresh login: allow the Dev-landing redirect to fire once for this session.
      try { sessionStorage.removeItem('wq_dev_landing_done') } catch { /* no-op */ }
    } catch (err) {
      const message = getErrorMessage(err, 'Login failed')
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    setIsAuthenticated(false)
    setCurrentAgent(null)
    setToken(null)
    localStorage.removeItem('auth_token')
    setError(null)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const updateAgent = useCallback((patch: Partial<Agent>) => {
    setCurrentAgent((prev) => (prev ? { ...prev, ...patch } : prev))
  }, [])

  const value: AuthContextType = {
    isAuthenticated,
    currentAgent,
    loading,
    error,
    login,
    logout,
    clearError,
    updateAgent,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const getAuthToken = () => {
  // Token is stored in AuthContext state, not localStorage
  // This is accessed through useAuth hook
  return null
}
